export const MAX_BULLETS = 4;

export type SummaryState = 'loading' | 'ready' | 'failed';

/**
 * The editable "What changed" list. What Henry leaves here is exactly what the
 * email and the client's What changed card say (mock-up 05 §02).
 */
export default function SummaryBullets({
  state,
  bullets,
  round,
  aiWritten,
  emailing,
  onChange,
  onRewrite,
}: {
  state: SummaryState;
  bullets: string[];
  round: number;
  aiWritten: boolean;
  emailing: boolean;
  onChange: (next: string[]) => void;
  onRewrite: () => void;
}) {
  const setAt = (i: number, value: string) => onChange(bullets.map((b, j) => (j === i ? value : b)));
  const drop = (i: number) => onChange(bullets.filter((_, j) => j !== i));
  const add = () => onChange([...bullets, '']);

  return (
    <div className="mt-3 border border-line rounded-[8px] bg-page">
      <div className="flex items-center justify-between px-3 py-2 border-b border-line">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">What changed</span>
        {state === 'loading' ? (
          <span className="text-[11.5px] text-faint">Reading your changes…</span>
        ) : (
          <button type="button" onClick={onRewrite} className="text-[12px] font-semibold text-accent hover:underline">
            {state === 'failed' ? 'Try again' : 'Rewrite'}
          </button>
        )}
      </div>

      {state === 'loading' ? (
        <div className="px-3 py-3 space-y-2" aria-hidden>
          {[82, 64, 74].map((w) => (
            <div key={w} className="h-2.5 rounded-full bg-elevated animate-pulse" style={{ width: `${w}%` }} />
          ))}
        </div>
      ) : (
        <>
          {state === 'failed' && bullets.length === 0 && (
            <p className="px-3 pt-3 text-[12.5px] text-muted leading-[1.5]">
              Couldn't write a summary this time. Add your own lines, or send without: the email then just says v{round} is ready.
            </p>
          )}
          {bullets.length > 0 && (
            <ul className="px-3 pt-2 pb-1 space-y-1">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 group">
                  <span aria-hidden className="mt-[9px] w-1 h-1 rounded-full bg-faint shrink-0" />
                  <textarea
                    value={b}
                    rows={1}
                    maxLength={300}
                    aria-label={`What changed, line ${i + 1}`}
                    onChange={(e) => setAt(i, e.target.value.replace(/\n/g, ' '))}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                    className="flex-1 min-w-0 resize-none bg-transparent text-[13px] leading-[1.5] text-fg rounded-[4px] px-1 -mx-1 py-0.5 focus:outline-none focus-visible:shadow-focus [field-sizing:content]"
                  />
                  <button
                    type="button"
                    onClick={() => drop(i)}
                    aria-label={`Remove line ${i + 1}`}
                    className="text-faint hover:text-fg text-[15px] leading-none px-1 pt-0.5"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-start justify-between gap-3 px-3 py-2 text-[11.5px] text-faint">
            <span>
              {!emailing
                ? "Still shown on the client's page."
                : aiWritten && bullets.length > 0
                ? 'Written by AI from your changes. Check every line: this goes to the client.'
                : 'These lines go to the client.'}
            </span>
            {bullets.length < MAX_BULLETS && (
              <button type="button" onClick={add} className="font-semibold text-accent hover:underline shrink-0">
                + Add a line
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
